import React, { useState } from 'react';
import { ArrowRight, Briefcase, Calendar, CheckCircle, Download, Linkedin, Mail, Send, Star, Target, Users } from 'lucide-react';

// Use relative URLs on production (same-origin) to avoid CORS issues
const BACKEND_URL = window.location.hostname === 'sierrascala.com' || window.location.hostname === 'www.sierrascala.com'
  ? ''
  : process.env.REACT_APP_BACKEND_URL;

const OptDigitalLandingPage = () => {
  const [accessGranted, setAccessGranted] = useState(() => {
    return typeof window !== 'undefined' && sessionStorage.getItem('optdigital_access') === 'granted';
  });
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState(false);
  const [requestName, setRequestName] = useState('');
  const [requestCompany, setRequestCompany] = useState('');
  const [requestEmail, setRequestEmail] = useState('');
  const [requestSent, setRequestSent] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [activePhase, setActivePhase] = useState(0);
  const [resumeDropdownOpen, setResumeDropdownOpen] = useState(false);

  const ResumeDropdown = () => (
    <div className="relative">
      <button
        onClick={() => setResumeDropdownOpen(!resumeDropdownOpen)}
        className="px-8 py-3 border-2 border-emerald-400 text-emerald-300 rounded-full font-semibold hover:bg-emerald-400/10 transition-all flex items-center gap-2"
        data-testid="resume-dropdown-toggle"
      >
        <Download size={20} /> Download Resume
      </button>
      {resumeDropdownOpen && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 flex flex-col bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden min-w-[260px] z-50">
          <a href="/Sierra_Scala_Resume_LocalSEO.html" download="Sierra_Scala_Resume_LocalSEO.html"
            className="px-4 py-3 text-sm text-gray-700 hover:bg-emerald-50 flex items-center gap-2 border-b border-slate-100"
            data-testid="download-resume-localseo"
            onClick={() => setResumeDropdownOpen(false)}>
            <Target size={16} className="text-emerald-600" /> Local SEO Specialist Resume
          </a>
          <a href="/Sierra_Scala_Resume.pdf" download
            className="px-4 py-3 text-sm text-gray-700 hover:bg-slate-50 flex items-center gap-2"
            data-testid="download-resume-general"
            onClick={() => setResumeDropdownOpen(false)}>
            <Briefcase size={16} className="text-slate-500" /> General Resume
          </a>
        </div>
      )}
    </div>
  );

  const ACCESS_CODE = 'optdigital2026';

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (codeInput.toLowerCase().trim() === ACCESS_CODE.toLowerCase()) {
      sessionStorage.setItem('optdigital_access', 'granted');
      setAccessGranted(true);
      setCodeError(false);
    } else {
      setCodeError(true);
    }
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    if (!requestName.trim() || !requestEmail.trim()) return;
    setRequestLoading(true);
    try {
      await fetch(`${BACKEND_URL}/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: requestName, email: requestEmail, company: requestCompany || '', proposal: 'optdigital' })
      });
    } catch (err) {}
    setRequestSent(true);
    setRequestLoading(false);
  };

  const phases = [
    { num: '01', title: 'Research & Foundation', weeks: 'Weeks 1-2', color: 'from-emerald-500 to-green-600',
      items: ['Semrush local keyword research (primary: "plumber [city]", service + location clusters)', 'Competitor Map Pack + organic audit', 'GBP claim/optimize (photos, posts, Q&A, services, categories)', 'NAP consistency check via Moz Local + Semrush', 'Master keyword map built in Google Sheets'] },
    { num: '02', title: 'On-Page & Schema Stacking', weeks: 'Weeks 2-4', color: 'from-blue-500 to-indigo-600',
      items: ['WordPress audit — Core Web Vitals, mobile-first, speed optimization', 'Create/optimize location pages + service pages with geo-keywords', 'Schema stacking: Organization + LocalBusiness + Service + Review + FAQPage + BreadcrumbList (JSON-LD)', 'Title tags, meta descriptions, headings, alt text with local keywords', 'Zapier automation: new page → ping GSC + update sitemap'] },
    { num: '03', title: 'Content Optimization & Creation', weeks: 'Weeks 3-6', color: 'from-purple-500 to-violet-600',
      items: ['8-12 new content pieces: location landing pages, "Best [Service] in [City]" posts, service clusters', 'On-page: natural keyword use, internal links, geo-images, E-E-A-T signals', 'Weekly GBP posts, YouTube videos, syndicated content', 'Semrush Content Template → brief writers → WordPress publish → schema per post', 'Make.com: RSS → auto-post to social + email newsletter'] },
    { num: '04', title: 'Map Stacking & Local Signals', weeks: 'Weeks 4-8', color: 'from-amber-500 to-orange-600',
      items: ['Custom Google Map with business pin + service area embedded on site', 'Build Google stack: Site, Doc, Sheet, Slide, Drive folder — all NAP + keyword optimized', 'Interlink stack assets → website → GBP for authority signals', 'Cloud stacking on high-authority platforms for entity reinforcement', 'Daily GBP content + review generation campaign'] },
    { num: '05', title: 'Automation & Scaling', weeks: 'Weeks 6+', color: 'from-cyan-500 to-teal-600',
      items: ['Make.com/n8n: review monitoring → auto-response + request more reviews', 'Rank drop alert (Semrush API) → auto-notify + trigger content refresh', 'Schema update and citation fix automation', 'GBP post scheduler for consistent posting', 'Template entire process so new clients launch in days, not weeks'] },
    { num: '06', title: 'Monitoring, Reporting & Iteration', weeks: 'Ongoing', color: 'from-rose-500 to-red-600',
      items: ['Weekly: Semrush position tracking + GA4 local traffic, calls, form fills', 'Monthly: full audit (schema validity, stack health, Map Pack GeoGrid screenshots)', 'Content refresh + stack updates based on ranking movement', 'Visual dashboards: Map Pack share, lead growth, call volume, revenue attribution', 'Plain-English reports clients actually understand'] }
  ];

  if (!accessGranted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-green-900 to-slate-900 flex items-center justify-center p-4" data-testid="optdigital-access-gate">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">SS</div>
            <h1 className="text-2xl font-bold text-white mb-2">Sierra Scala</h1>
            <p className="text-emerald-300 text-sm">Local SEO Specialist Proposal for Opt Digital</p>
          </div>
          <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-8 border border-white/40 mb-6" data-testid="access-code-card">
            <h2 className="text-xl font-bold text-white text-center mb-2">This Proposal is Private</h2>
            <p className="text-emerald-200 text-sm text-center mb-6">Enter the access code to view the Local SEO strategy.</p>
            <form onSubmit={handleCodeSubmit}>
              <input type="text" value={codeInput} onChange={(e) => setCodeInput(e.target.value)} placeholder="Enter access code"
                className={`w-full px-4 py-3 bg-white/30 border ${codeError ? 'border-red-500' : 'border-white/50'} rounded-xl text-white placeholder-white/70 focus:outline-none focus:border-emerald-400 transition-colors text-center tracking-widest`}
                data-testid="optdigital-access-input" />
              {codeError && <p className="text-red-400 text-xs text-center mt-2" data-testid="access-code-error">Incorrect code. Please try again.</p>}
              <button type="submit" className="w-full py-3 mt-4 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold rounded-xl transition-all" data-testid="optdigital-code-submit">View Proposal</button>
            </form>
          </div>
          <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 border border-white/35">
            {!requestSent ? (
              <form onSubmit={handleRequestAccess}>
                <h3 className="text-white font-semibold text-center mb-1">Don't have the code?</h3>
                <p className="text-emerald-300 text-xs text-center mb-4">Request access and I'll send it to you directly.</p>
                <div className="space-y-3">
                  <input type="text" value={requestName} onChange={(e) => setRequestName(e.target.value)} placeholder="Your Name" className="w-full px-4 py-2.5 bg-white/30 border border-white/50 rounded-lg text-white placeholder-white/70 focus:outline-none focus:border-emerald-400 text-sm" required />
                  <input type="email" value={requestEmail} onChange={(e) => setRequestEmail(e.target.value)} placeholder="Email Address" className="w-full px-4 py-2.5 bg-white/30 border border-white/50 rounded-lg text-white placeholder-white/70 focus:outline-none focus:border-emerald-400 text-sm" required />
                  <input type="text" value={requestCompany} onChange={(e) => setRequestCompany(e.target.value)} placeholder="Company (optional)" className="w-full px-4 py-2.5 bg-white/30 border border-white/50 rounded-lg text-white placeholder-white/70 focus:outline-none focus:border-emerald-400 text-sm" />
                  <button type="submit" disabled={requestLoading} className="w-full py-2.5 bg-white/30 hover:bg-white/40 border border-white/50 text-white font-medium rounded-lg transition-all text-sm">
                    {requestLoading ? 'Sending...' : 'Request Access'}
                  </button>
                </div>
                <div className="mt-4 pt-4 border-t border-white/20 text-center">
                  <p className="text-emerald-300 text-xs mb-2">Or reach out directly:</p>
                  <a href="mailto:me@sierrascala.com" className="text-white text-sm hover:text-emerald-300">me@sierrascala.com</a>
                </div>
              </form>
            ) : (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-3"><CheckCircle className="text-emerald-400" size={24} /></div>
                <h3 className="text-white font-semibold mb-1">Request Sent!</h3>
                <p className="text-emerald-300 text-sm">I'll send you the access code shortly.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen" data-testid="optdigital-proposal-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 text-white py-20 px-4 sm:px-6 lg:px-8" data-testid="optdigital-hero">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-500/20 border border-emerald-500/30 rounded-full mb-6">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
            <span className="text-emerald-300 text-xs font-semibold uppercase tracking-wider">Local SEO Specialist Application</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-emerald-400 to-green-300 bg-clip-text text-transparent">Local SEO Strategy</span>
            <br />for Opt Digital
          </h1>
          <p className="text-xl text-emerald-100 max-w-3xl mx-auto mb-4">
            Client delivery, campaign execution, and relationship management — from onboarding to Map Pack domination for trades & service businesses.
          </p>
          <p className="text-slate-400 text-sm">Sierra Scala | Dallas-Fort Worth, TX | sierrascala.com</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <a href="#strategy" className="px-8 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-semibold transition-all flex items-center gap-2"><ArrowRight size={18} /> View Strategy</a>
            <a href="https://www.linkedin.com/in/sierra-scala-62b9a0254" target="_blank" rel="noopener noreferrer" className="px-8 py-3 border-2 border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition-all">Connect on LinkedIn</a>
          </div>
        </div>
      </section>

      {/* Why I'm a Fit */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="optdigital-fit">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Why I'm the Right Fit</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2">Local SEO + Client Management + Execution</h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">I bring hands-on Local SEO experience combined with the client-facing and project management skills this role demands.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <div className="bg-gradient-to-b from-emerald-50 to-white rounded-2xl p-6 border border-emerald-100 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">5+</div>
              <p className="text-sm font-medium text-gray-900">Years in Digital Marketing</p>
              <p className="text-xs text-gray-500 mt-1">Social media, PPC, SEO, content strategy</p>
            </div>
            <div className="bg-gradient-to-b from-emerald-50 to-white rounded-2xl p-6 border border-emerald-100 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">850K+</div>
              <p className="text-sm font-medium text-gray-900">Campaign Reach</p>
              <p className="text-xs text-gray-500 mt-1">Geo-targeted local campaigns with 200+ qualified leads</p>
            </div>
            <div className="bg-gradient-to-b from-emerald-50 to-white rounded-2xl p-6 border border-emerald-100 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">75%</div>
              <p className="text-sm font-medium text-gray-900">CPC Reduction</p>
              <p className="text-xs text-gray-500 mt-1">Optimized geo-targeted PPC for local service businesses</p>
            </div>
            <div className="bg-gradient-to-b from-emerald-50 to-white rounded-2xl p-6 border border-emerald-100 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">68%</div>
              <p className="text-sm font-medium text-gray-900">Foot-Traffic Lift</p>
              <p className="text-xs text-gray-500 mt-1">Local content campaigns driving real-world results</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Target size={18} className="text-emerald-600" /> Local SEO Experience</h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2"><CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" /> <span>Hands-on Local SEO and geo-targeting with <strong>Web-TechServices.com</strong> — GMB optimization, local citations, location/service pages, schema markup</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" /> <span>Geo-targeted PPC campaigns for DFW local businesses — reducing CPC by 75%+ while driving calls and foot traffic</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" /> <span>Content optimization for local search: location-specific landing pages, "near me" keyword targeting, local blog content</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" /> <span>On-page SEO: title tags, meta descriptions, internal linking, alt text optimization, Core Web Vitals</span></li>
              </ul>
            </div>
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Users size={18} className="text-emerald-600" /> Client & Team Management</h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2"><CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" /> <span>Managing multi-client campaigns concurrently — coordinating content writers, designers, and developers</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" /> <span>Client onboarding, expectation setting, and proactive communication (calls, emails, reporting)</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" /> <span>Translating complex marketing data into plain-English reports business owners understand</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" /> <span>Marketing Manager at Ninja Technologies — leading multi-platform strategy, PPC, and team coordination</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Tools & Tech Stack */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 to-emerald-950" data-testid="optdigital-tools">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Tech Stack</span>
            <h2 className="text-3xl font-bold text-white mt-2">Tools I Use Daily</h2>
            <p className="text-emerald-200 mt-3">Aligned with Opt Digital's stack — plus extras that drive results.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {[
              { name: 'Semrush', cat: 'SEO & Research' }, { name: 'Moz', cat: 'Local SEO' }, { name: 'Diib', cat: 'Analytics' },
              { name: 'Semalt', cat: 'SEO Platform' }, { name: 'Google Analytics (GA4)', cat: 'Analytics' }, { name: 'Google Search Console', cat: 'Webmaster' },
              { name: 'BrightLocal', cat: 'Local SEO' }, { name: 'Local Viking', cat: 'GeoGrid Tracking' }, { name: 'WordPress', cat: 'CMS' },
              { name: 'Rank Math / Yoast', cat: 'SEO Plugins' }, { name: 'Make.com / Zapier', cat: 'Automation' }, { name: 'n8n', cat: 'Automation' },
              { name: 'ClickUp', cat: 'Project Mgmt' }, { name: 'Google Workspace', cat: 'Collaboration' }, { name: 'Meta Business Suite', cat: 'Social' },
              { name: 'Hootsuite / Later', cat: 'Scheduling' }, { name: 'Canva / Adobe', cat: 'Design' }, { name: 'CapCut', cat: 'Video' },
              { name: 'ChatGPT', cat: 'AI Content' }, { name: 'Google Ads', cat: 'PPC' }
            ].map(tool => (
              <div key={tool.name} className="bg-white/10 rounded-xl p-3 border border-white/10 text-center hover:bg-white/15 transition-all">
                <p className="text-white text-sm font-medium">{tool.name}</p>
                <p className="text-emerald-300 text-xs">{tool.cat}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6-Phase Campaign Framework */}
      <section id="strategy" className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="optdigital-phases">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Campaign Framework</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2">6-Phase Local SEO Campaign</h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">The structured, execution-focused framework I'd implement for every Opt Digital client — from foundation to Map Pack domination.</p>
          </div>

          {/* Phase Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {phases.map((phase, i) => (
              <button key={i} onClick={() => setActivePhase(i)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activePhase === i ? `bg-gradient-to-r ${phase.color} text-white shadow-lg` : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                data-testid={`phase-tab-${i}`}>
                Phase {phase.num}: {phase.title}
              </button>
            ))}
          </div>

          {/* Active Phase Detail */}
          <div className="bg-gradient-to-br from-slate-50 to-emerald-50 rounded-2xl p-8 border border-slate-200" data-testid="phase-detail">
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-14 h-14 bg-gradient-to-r ${phases[activePhase].color} rounded-xl flex items-center justify-center text-white font-bold text-lg`}>
                {phases[activePhase].num}
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{phases[activePhase].title}</h3>
                <p className="text-sm text-emerald-600 font-medium">{phases[activePhase].weeks}</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {phases[activePhase].items.map((item, j) => (
                <div key={j} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-slate-100">
                  <CheckCircle size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">{item}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Phase Timeline Visual */}
          <div className="mt-8 flex flex-wrap justify-center gap-1">
            {phases.map((phase, i) => (
              <div key={i} className={`flex items-center gap-1 ${i < phases.length - 1 ? '' : ''}`}>
                <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${phase.color} flex items-center justify-center text-white font-bold text-xs cursor-pointer hover:scale-110 transition-transform ${activePhase === i ? 'ring-4 ring-emerald-200' : ''}`}
                  onClick={() => setActivePhase(i)}>{phase.num}</div>
                {i < phases.length - 1 && <div className="w-8 h-0.5 bg-slate-300" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How I'd Manage Clients */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 to-emerald-950" data-testid="optdigital-client-mgmt">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Client Delivery</span>
            <h2 className="text-3xl font-bold text-white mt-2">How I'd Manage 20-30 Accounts</h2>
            <p className="text-emerald-200 mt-3 max-w-2xl mx-auto">Structured systems for proactive communication and consistent delivery at scale.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center mb-4"><Calendar size={20} className="text-white" /></div>
              <h3 className="text-white font-bold mb-2">Weekly Rhythm</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-emerald-400 mt-0.5" /> Monday: Review all client dashboards, flag drops/wins</li>
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-emerald-400 mt-0.5" /> Tues-Thurs: Client calls + strategy execution blocks</li>
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-emerald-400 mt-0.5" /> Friday: Internal coordination + next-week prep</li>
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-emerald-400 mt-0.5" /> Proactive updates before clients ask</li>
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mb-4"><Send size={20} className="text-white" /></div>
              <h3 className="text-white font-bold mb-2">Communication</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-emerald-400 mt-0.5" /> Monthly video call: full performance review</li>
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-emerald-400 mt-0.5" /> Bi-weekly email updates with ranking snapshots</li>
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-emerald-400 mt-0.5" /> SMS for quick wins ("You're now #2 for [keyword]!")</li>
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-emerald-400 mt-0.5" /> Plain-English reporting — no jargon</li>
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center mb-4"><Star size={20} className="text-white" /></div>
              <h3 className="text-white font-bold mb-2">Client Onboarding</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-emerald-400 mt-0.5" /> Kickoff call: understand business, goals, competitors</li>
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-emerald-400 mt-0.5" /> Set clear expectations: timeline, deliverables, KPIs</li>
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-emerald-400 mt-0.5" /> Align with content, design, dev teams on day one</li>
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-emerald-400 mt-0.5" /> First-week quick win to build immediate trust</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Opt Digital's Model */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="optdigital-understanding">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Your Vision</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2">I Understand What Opt Digital Is Building</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-100">
              <h3 className="font-bold text-gray-900 mb-3">The Goal</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2"><ArrowRight size={14} className="text-emerald-600 mt-0.5" /> $300K/month recurring revenue within 3 months</li>
                <li className="flex items-start gap-2"><ArrowRight size={14} className="text-emerald-600 mt-0.5" /> 10-20 new clients onboarded monthly</li>
                <li className="flex items-start gap-2"><ArrowRight size={14} className="text-emerald-600 mt-0.5" /> Most trusted Local SEO name across NZ, USA, and Australia</li>
                <li className="flex items-start gap-2"><ArrowRight size={14} className="text-emerald-600 mt-0.5" /> Roofers, electricians, plumbers, landscapers, fencers — trades that live and die by local search</li>
              </ul>
            </div>
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-100">
              <h3 className="font-bold text-gray-900 mb-3">How I'll Contribute</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2"><ArrowRight size={14} className="text-emerald-600 mt-0.5" /> Own client-side delivery and campaign accountability</li>
                <li className="flex items-start gap-2"><ArrowRight size={14} className="text-emerald-600 mt-0.5" /> Drive retention through proactive communication and results</li>
                <li className="flex items-start gap-2"><ArrowRight size={14} className="text-emerald-600 mt-0.5" /> Execute the 6-phase campaign model with schema stacking, map stacking, and content optimization</li>
                <li className="flex items-start gap-2"><ArrowRight size={14} className="text-emerald-600 mt-0.5" /> Build automations that let us scale from 20 clients to 50+ without quality loss</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 bg-gradient-to-r from-emerald-600 to-green-700 rounded-2xl p-6 text-white text-center">
            <p className="text-lg font-medium">
              "I don't just relay information from the client to the team — I lead the charge and implement the gameplan."
            </p>
            <p className="text-emerald-200 text-sm mt-2">That line from your job description resonated with me. That's exactly how I work.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-slate-900 via-emerald-950 to-slate-900" data-testid="optdigital-cta">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Let's Talk About How I Can Help Opt Digital Scale</h2>
          <p className="text-xl text-emerald-200 mb-8">I'm ready to own client delivery, execute your 3-month campaign model, and help you reach $300K/month in recurring revenue.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="mailto:me@sierrascala.com" className="px-8 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-semibold hover:shadow-lg transition-all flex items-center gap-2"><Mail size={20} /> me@sierrascala.com</a>
            <a href="https://www.linkedin.com/in/sierra-scala-62b9a0254" target="_blank" rel="noopener noreferrer" className="px-8 py-3 border-2 border-white text-white rounded-full font-semibold hover:bg-white/10 transition-all flex items-center gap-2"><Linkedin size={20} /> Connect on LinkedIn</a>
            <ResumeDropdown />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 bg-slate-900 text-center">
        <p className="text-slate-400 text-sm">
          &copy; {new Date().getFullYear()} Sierra Scala | <a href="/" className="text-emerald-400 hover:text-emerald-300">View Full Portfolio</a>
        </p>
      </footer>
    </div>
  );
};


// Admin Dashboard Component

export default OptDigitalLandingPage;
